import { Tooltip } from '@/components/ui/tooltip';
import { Box, Button, HStack, Stack, Text } from '@chakra-ui/react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Everything a companion card looks like except its evidence.
 *
 * The frame and the action row were written out once per kind, and the family
 * had already forked: two cards were missing a prop the other three passed, and
 * the generic card said "ignore" through a second translation key. A card now
 * contributes its evidence and nothing else, so restyling the call to action is
 * one edit rather than six that have to be remembered together.
 */

/** One line of what the companion saw. Never a figure the evidence does not carry. */
export const EvidenceLine = ({ children }: { children: ReactNode }) => (
  <Text fontSize="12.5px" color="inkMuted" lineHeight="1.5">
    {children}
  </Text>
);

export interface CompanionCardFooterProps {
  /** The words the server chose for the gesture. The card never rewords them. */
  ctaLabel: string;
  onOpenCta: () => void;
  /** Starts the dismissal choice. Absent while the panel does not offer it. */
  onDismiss?: () => void;
  isCtaPending?: boolean;
  /** The workflow has no adapter in this build; the button is shown inert. */
  isCtaDisabled?: boolean;
  /**
   * Explains the inert button on hover. Only the generic card passes one: the
   * kinds that ship with a presenter always name a workflow the client knows.
   */
  ctaDisabledHint?: string;
  /** The host's own controls, rendered at the end of the action row. */
  footerEnd?: ReactNode;
}

/** The action row every card ends on: open, ignore, and whatever the host adds. */
export const CompanionCardFooter = ({
  ctaLabel,
  onOpenCta,
  onDismiss,
  isCtaPending = false,
  isCtaDisabled = false,
  ctaDisabledHint,
  footerEnd,
}: CompanionCardFooterProps) => {
  const { t } = useTranslation();

  const cta = (
    <Button
      variant="solid"
      bg="darkNav"
      color="white"
      h="36px"
      px="15px"
      borderRadius="8px"
      fontFamily="heading"
      fontSize="13.5px"
      fontWeight="600"
      _hover={{ bg: 'ink' }}
      onClick={onOpenCta}
      loading={isCtaPending}
      disabled={isCtaDisabled}
    >
      {ctaLabel}
    </Button>
  );

  return (
    <HStack gap="10px" align="center">
      {ctaDisabledHint ? (
        <Tooltip
          content={ctaDisabledHint}
          disabled={!isCtaDisabled}
          positioning={{ placement: 'top' }}
        >
          {/* A disabled button fires no pointer event, so the tooltip needs a
              wrapper that does. */}
          <Box>{cta}</Box>
        </Tooltip>
      ) : (
        cta
      )}
      {onDismiss ? (
        <Button
          variant="plain"
          h="36px"
          px="0"
          fontFamily="heading"
          fontSize="13.5px"
          fontWeight="600"
          color="inkMuted"
          _hover={{ color: 'ink' }}
          onClick={onDismiss}
        >
          {t('companion.cards.dismiss')}
        </Button>
      ) : null}
      {footerEnd ? <Box ml="auto">{footerEnd}</Box> : null}
    </HStack>
  );
};

export interface CompanionCardShellProps extends CompanionCardFooterProps {
  /** How the rest of the dashboard finds this card. */
  testId: string;
  /**
   * What the card stamps on its frame so a click elsewhere can close it: the
   * batch, the mention, the authorization it named.
   */
  dataAttrs?: Record<string, string>;
  /** Fills the panel edge to edge: no frame of its own, the panel is the frame. */
  flat?: boolean;
  /** The header and the evidence, in the order the card states them. */
  children: ReactNode;
}

export const CompanionCardShell = ({
  testId,
  dataAttrs,
  flat = false,
  children,
  ...footer
}: CompanionCardShellProps) => (
  <Stack
    gap="14px"
    p="18px"
    pb="16px"
    bg="panelBg"
    borderWidth={flat ? 0 : '1px'}
    borderColor="cardBorder"
    borderRadius={flat ? '0' : '14px'}
    data-testid={testId}
    {...dataAttrs}
  >
    {children}
    <CompanionCardFooter {...footer} />
  </Stack>
);
